import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import { PageShell } from '../../components/layout/page/PageShell';
import { PortalProjectActivities } from './PortalProjectActivities';
import { PortalProjectDeliveries } from './PortalProjectDeliveries';
import { PortalProjectFiles } from './PortalProjectFiles';
import { PortalProjectOverview } from './PortalProjectOverview';
import { PortalProjectPlanning } from './PortalProjectPlanning';
import { PortalProjectRisksChanges } from './PortalProjectRisksChanges';
import { PortalProjectTeam } from './PortalProjectTeam';
import { portalActivitiesByProjectId } from './portalActivitiesMock';
import { portalDeliveriesByProjectId } from './portalDeliveriesMock';
import { portalFilesByProjectId } from './portalFilesMock';
import { portalOverviewByProjectId } from './portalOverviewMock';
import { portalPlanningByProjectId } from './portalPlanningMock';
import { portalRisksChangesByProjectId } from './portalRisksChangesMock';
import { portalTeamByProjectId } from './portalTeamMock';
import { usePortalState } from './PortalState';

const projectTabs = [
  { id: 'overview', label: 'Visão Geral' },
  { id: 'planning', label: 'Planejamento' },
  { id: 'activities', label: 'Atividades' },
  { id: 'deliveries', label: 'Entregas' },
  { id: 'team', label: 'Equipe' },
  { id: 'risks-changes', label: 'Riscos e Mudanças' },
  { id: 'files', label: 'Arquivos' },
  { id: 'reports', label: 'Relatórios' },
] as const;

type ProjectTab = (typeof projectTabs)[number]['id'];

function isProjectTab(value: string | null): value is ProjectTab {
  return projectTabs.some((tab) => tab.id === value);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

export function PortalProjectPage() {
  const { projectId: routeProjectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data } = usePortalState();
  const project = data.projects.find((item) => item.id === routeProjectId);
  const overview = project ? portalOverviewByProjectId[project.id] : undefined;
  const planning = project ? portalPlanningByProjectId[project.id] : undefined;
  const activities = project ? portalActivitiesByProjectId[project.id] : undefined;
  const deliveries = project ? portalDeliveriesByProjectId[project.id] : undefined;
  const team = project ? portalTeamByProjectId[project.id] : undefined;
  const risksChanges = project ? portalRisksChangesByProjectId[project.id] : undefined;
  const projectFiles = project ? portalFilesByProjectId[project.id] : undefined;
  const requestedTab = searchParams.get('tab');
  const tab: ProjectTab = isProjectTab(requestedTab) ? requestedTab : 'overview';
  const activeTabLabel = projectTabs.find((item) => item.id === tab)?.label ?? tab;
  const projectPath = project ? `/portal/projects/${project.id}` : '/portal/projects';
  const projectName = overview?.name ?? project?.name ?? '';
  const statusLabel = overview?.statusLabel ?? project?.statusLabel ?? '';
  const description = overview?.description ?? project?.summary ?? '';

  if (!project) {
    return (
      <PageShell>
        <p className="text-sm text-secondary-700">Projeto não encontrado.</p>
        <Link to="/portal/projects" className="mt-3 inline-block text-sm font-semibold text-primary-700">
          Voltar aos projetos
        </Link>
      </PageShell>
    );
  }

  function openTab(next: ProjectTab) {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params);
  }

  return (
    <PageShell>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <nav className="text-sm text-secondary-500">
            <Link to="/portal/projects" className="font-medium text-secondary-600 hover:text-primary-700">
              Meus Projetos
            </Link>
            <span className="mx-2 text-secondary-300">›</span>
            <Link to={projectPath} className="font-medium text-secondary-600 hover:text-primary-700">
              {projectName}
            </Link>
            {tab !== 'overview' ? (
              <>
                <span className="mx-2 text-secondary-300">›</span>
                <span className="font-medium text-secondary-900">{activeTabLabel}</span>
              </>
            ) : null}
          </nav>
          {tab === 'planning' ? (
            <button
              type="button"
              onClick={() => openTab('overview')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm font-semibold text-secondary-700 hover:bg-secondary-50"
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden />
              Ver visão geral
            </button>
          ) : null}
        </div>

        <header className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-secondary-900 md:text-3xl">{projectName}</h1>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              {statusLabel}
            </span>
          </div>
          <p className="max-w-4xl text-sm leading-relaxed text-secondary-600">{description}</p>
          {tab === 'overview' && overview ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <MetaItem icon={BuildingOffice2Icon} label="Cliente" value={overview.clientName} />
              <MetaItem icon={CalendarDaysIcon} label="Início" value={overview.startLabel} />
              <MetaItem icon={FlagIcon} label="Previsão de término" value={overview.endLabel} />
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">
                  {initials(overview.ownerName)}
                </span>
                <div>
                  <p className="text-xs font-medium text-secondary-500">Responsável na Baruk</p>
                  <p className="text-sm font-semibold text-secondary-900">{overview.ownerName}</p>
                </div>
              </div>
            </div>
          ) : null}
        </header>

        <div className="border-b border-secondary-200">
          <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="Abas do projeto">
            {projectTabs.map((item) => {
              const active = tab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openTab(item.id)}
                  className={`shrink-0 rounded-none border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                    active
                      ? 'border-primary-500 text-primary-700'
                      : 'border-transparent text-secondary-600 hover:border-secondary-300 hover:text-secondary-900'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>

        {tab === 'overview' ? (
          overview ? (
            <PortalProjectOverview overview={overview} />
          ) : (
            <ProjectTabPlaceholder label="Visão Geral" />
          )
        ) : null}
        {tab === 'planning' ? (
          planning ? (
            <PortalProjectPlanning planning={planning} projectPath={projectPath} />
          ) : (
            <ProjectTabPlaceholder label="Planejamento" />
          )
        ) : null}
        {tab === 'activities' ? (
          activities ? (
            <PortalProjectActivities activities={activities} projectPath={projectPath} />
          ) : (
            <ProjectTabPlaceholder label="Atividades" />
          )
        ) : null}
        {tab === 'deliveries' ? (
          deliveries ? (
            <PortalProjectDeliveries deliveries={deliveries} />
          ) : (
            <ProjectTabPlaceholder label="Entregas" />
          )
        ) : null}
        {tab === 'team' ? (
          team ? (
            <PortalProjectTeam team={team} />
          ) : (
            <ProjectTabPlaceholder label="Equipe" />
          )
        ) : null}
        {tab === 'risks-changes' ? (
          risksChanges ? (
            <PortalProjectRisksChanges data={risksChanges} />
          ) : (
            <ProjectTabPlaceholder label="Riscos e Mudanças" />
          )
        ) : null}
        {tab === 'files' ? (
          projectFiles ? (
            <PortalProjectFiles filesData={projectFiles} />
          ) : (
            <ProjectTabPlaceholder label="Arquivos" />
          )
        ) : null}
        {tab !== 'overview' &&
        tab !== 'planning' &&
        tab !== 'activities' &&
        tab !== 'deliveries' &&
        tab !== 'team' &&
        tab !== 'risks-changes' &&
        tab !== 'files' ? (
          <ProjectTabPlaceholder label={activeTabLabel} />
        ) : null}
      </div>
    </PageShell>
  );
}

function MetaItem({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof BuildingOffice2Icon;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <div>
        <p className="text-xs font-medium text-secondary-500">{label}</p>
        <p className="text-sm font-semibold text-secondary-900">{value}</p>
      </div>
    </div>
  );
}

function ProjectTabPlaceholder({ label }: { label: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-secondary-300 bg-white px-4 py-12 text-center">
      <h2 className="text-lg font-semibold text-secondary-900">{label}</h2>
      <p className="mt-2 text-sm text-secondary-500">Conteúdo em construção — mock visual na próxima entrega.</p>
    </div>
  );
}
